/* eslint-disable react-refresh/only-export-components -- this file is the
   route-config module: it defines the app's layout components (Sidebar,
   AdminLayout) alongside the exported `router` object by design. */
import { lazy, Suspense, useEffect, useState } from 'react'
import { createBrowserRouter, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Users, FileText, Trophy, Brain, Layers, BarChart3,
  CreditCard, Gift, Settings, Menu, X, LogOut, TrendingUp, RefreshCw, ChevronRight, Scissors, MessageCircle, Lightbulb, Gauge,
  Activity, Inbox, Banknote, Coins,
} from 'lucide-react'
import { useAdminAuth } from '@/store/authStore'
import { useToast } from '@/hooks/useToast'
import { ToastContainer } from '@/components/ToastContainer'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { cn } from './utils'
import { LogoMark } from '@/components/LogoMark'

// ─── Lazy pages ───────────────────────────────────────────────────────────────
const LoginPage              = lazy(() => import('@/pages/LoginPage'))
const DashboardPage          = lazy(() => import('@/pages/DashboardPage'))
const UsersPage              = lazy(() => import('@/pages/UsersPage'))
const SlipsPage              = lazy(() => import('@/pages/SlipsPage'))
const LeaguesPage            = lazy(() => import('@/pages/LeaguesPage'))
const PredictionsPage        = lazy(() => import('@/pages/PredictionsPage'))
const SlipReviewPage         = lazy(() => import('@/pages/SlipReviewPage'))
const AnalyticsPage          = lazy(() => import('@/pages/AnalyticsPage'))
const SubscriptionsPage      = lazy(() => import('@/pages/SubscriptionsPage'))
const ReferralsPage          = lazy(() => import('@/pages/ReferralsPage'))
const SettingsPage           = lazy(() => import('@/pages/SettingsPage'))
const AffiliateManagementPage = lazy(() => import('@/pages/AffiliateManagementPage'))
const SaaSMetricsPage        = lazy(() => import('@/pages/SaaSMetricsPage'))
const WinBackCampaignsPage   = lazy(() => import('@/pages/WinBackCampaigns'))
const TrapAnalyticsPage      = lazy(() => import('@/pages/TrapAnalyticsPage'))
const FeedbackPage           = lazy(() => import('@/pages/FeedbackPage'))
const InsightsPage           = lazy(() => import('@/pages/InsightsPage'))
const CalibrationPage        = lazy(() => import('@/pages/CalibrationPage'))
const AuditPage              = lazy(() => import('@/pages/AuditPage'))
const ContactInboxPage       = lazy(() => import('@/pages/ContactInboxPage'))
const RefundsPage            = lazy(() => import('@/pages/RefundsPage'))
const ArenaEconomyPage       = lazy(() => import('@/pages/ArenaEconomyPage'))
const NotFoundPage           = lazy(() => import('@/pages/NotFoundPage'))

// Auth guard lives in @/components/ProtectedRoute — it gates on store
// rehydration so a hard refresh doesn't bounce the admin to /login before
// localStorage is read.

// ─── Nav config ───────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: 'Core',
    items: [
      { to: '/admin',       label: 'Dashboard',   icon: LayoutDashboard, exact: true },
      { to: '/admin/users', label: 'Users',        icon: Users },
    ],
  },
  {
    label: 'Arena',
    items: [
      { to: '/admin/arena-economy', label: 'Arena Economy', icon: Coins },
    ],
  },
  {
    label: 'Content',
    items: [
      { to: '/admin/predictions', label: 'Predictions', icon: Brain },
      { to: '/admin/slips',       label: 'Slips',        icon: FileText },
      { to: '/admin/slip-review', label: 'Slip Review',  icon: Layers },
      { to: '/admin/leagues',     label: 'Leagues',      icon: Trophy },
    ],
  },
  {
    label: 'Revenue',
    items: [
      { to: '/admin/subscriptions', label: 'Subscriptions', icon: CreditCard },
      { to: '/admin/refunds',       label: 'Refunds',       icon: Banknote },
      { to: '/admin/affiliates',    label: 'Affiliates',    icon: Gift },
      { to: '/admin/referrals',     label: 'Referrals',     icon: RefreshCw },
    ],
  },
  {
    label: 'Growth',
    items: [
      { to: '/admin/analytics',    label: 'Analytics',    icon: BarChart3 },
      { to: '/admin/saas-metrics', label: 'SaaS Metrics', icon: TrendingUp },
      { to: '/admin/winback',      label: 'WinBack',      icon: ChevronRight },
      { to: '/admin/trap',         label: 'Trap-Stripper', icon: Scissors },
      { to: '/admin/feedback',     label: 'Feedback',     icon: MessageCircle },
      { to: '/admin/contact',      label: 'Contact Inbox', icon: Inbox },
      { to: '/admin/insights',     label: 'Insights',     icon: Lightbulb },
      { to: '/admin/calibration',  label: 'Calibration',  icon: Gauge },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/admin/audit',    label: 'Audit Log', icon: Activity },
      { to: '/admin/settings', label: 'Settings',  icon: Settings },
    ],
  },
]

const MOBILE_NAV = [
  { to: '/admin',               label: 'Home',     icon: LayoutDashboard, exact: true },
  { to: '/admin/users',         label: 'Users',    icon: Users },
  { to: '/admin/predictions',   label: 'Picks',    icon: Brain },
  { to: '/admin/subscriptions', label: 'Subs',     icon: CreditCard },
  { to: '/admin/analytics',     label: 'Stats',    icon: BarChart3 },
  { to: '/admin/settings',      label: 'Settings', icon: Settings },
]

function matchActive(pathname: string, to: string, exact?: boolean) {
  if (exact || to === '/admin') return pathname === to
  return pathname.startsWith(to)
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────
function Sidebar({ onClose }: { onClose?: () => void }) {
  const { user, logout } = useAdminAuth()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const go = (to: string) => { navigate(to); onClose?.() }

  return (
    <aside
      className="flex h-full flex-col border-r border-border/70 backdrop-blur-xl"
      style={{
        background: "hsl(var(--card) / 0.92)",
        boxShadow: "18px 0 60px -52px var(--skeuo-shadow)",
      }}
    >
      {/* Logo */}
      <div
        className="flex items-center gap-3 px-4 py-[14px]"
        style={{
          background: "hsl(var(--surface) / 0.86)",
          boxShadow: "0 1px 0 0 var(--skeuo-highlight) inset, 0 1px 0 hsl(var(--border) / 0.6)",
        }}
      >
        <LogoMark size={32} className="flex-shrink-0 rounded-lg" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-black text-text tracking-widest uppercase leading-none">Spinex</p>
          <p className="text-[9px] text-muted-foreground font-semibold tracking-widest uppercase mt-0.5 opacity-70">Admin Panel</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-accent/30 hover:text-text"
            style={{
              background: "hsl(var(--card) / 0.82)",
              boxShadow: "0 1px 0 0 var(--skeuo-highlight) inset",
            }}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 overflow-y-auto px-2 space-y-3 scrollbar-thin">
        {NAV_SECTIONS.map((section) => (
          <div key={section.label}>
            <p className="px-3 mb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-muted/50">
              {section.label}
            </p>
            <div className="space-y-0.5">
              {section.items.map(({ to, label, icon: Icon, exact }) => {
                const active = matchActive(pathname, to, exact)
                return (
                  <button
                    key={to}
                    onClick={() => go(to)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-[12px] font-medium transition-all duration-150',
                      active ? 'font-semibold text-info' : 'text-muted-foreground hover:bg-accent/25 hover:text-text',
                    )}
                    style={active ? {
                      background: "hsl(var(--accent))",
                      boxShadow: "0 1px 0 0 var(--skeuo-highlight) inset",
                    } : undefined}
                  >
                    <Icon className={cn(
                      'w-[14px] h-[14px] flex-shrink-0',
                      active ? 'text-info' : 'text-muted/70',
                    )} />
                    {label}
                    {active && (
                      <span
                        className="ml-auto w-1.5 h-1.5 rounded-full"
                        style={{ background: "hsl(var(--info))", boxShadow: "0 0 4px hsl(var(--info) / 0.45)" }}
                      />
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User profile */}
      <div
        className="p-3"
        style={{
          borderTop: "1px solid hsl(var(--border) / 0.6)",
          boxShadow: "0 -1px 0 0 rgba(255,255,255,0.04) inset",
        }}
      >
        <div className="flex items-center gap-2.5 rounded-lg border border-border/60 bg-surface/40 px-2 py-2">
          <div
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-info"
              style={{
                background: "hsl(var(--accent))",
                border: "1px solid hsl(var(--info) / 0.25)",
              }}
            >
            {user?.email?.slice(0, 2).toUpperCase() ?? 'AD'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-text truncate leading-tight">{user?.email}</p>
            <p className="text-[10px] text-muted-foreground capitalize">{user?.role}</p>
          </div>
          <button
            onClick={() => { logout(); navigate('/admin/login') }}
            title="Sign out"
            className="text-muted-foreground hover:text-danger p-1.5 rounded-lg hover:bg-danger/10 transition-all flex-shrink-0"
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </aside>
  )
}

// ─── Admin Layout ─────────────────────────────────────────────────────────────
function AdminLayout() {
  const { pathname } = useLocation()
  const { toasts, removeToast } = useToast()
  const [mobileOpen, setMobileOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  useEffect(() => { setMobileOpen(false) }, [pathname])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const typing = target && (
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable
      )
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'
      if ((e.key === '/' && !typing) || cmdK) {
        const search = document.querySelector<HTMLInputElement>(
          'input[placeholder*="Search" i], input[placeholder*="search" i]',
        )
        if (search) {
          e.preventDefault()
          search.focus()
          search.select()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const currentItem = NAV_SECTIONS.flatMap((s) => s.items).find(
    (item) => matchActive(pathname, item.to, item.exact),
  )

  return (
    <div className="admin-shell flex min-h-screen bg-bg">
      <div className="hidden md:block w-[220px] flex-shrink-0">
        <div className="fixed inset-y-0 left-0 w-[220px] z-30">
          <Sidebar />
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-bg/75 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="relative w-64 h-full">
            <Sidebar onClose={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        <header
          className="sticky top-0 z-20 h-12 flex items-center px-4 md:px-6 gap-3"
          style={{
            background: "hsl(var(--background) / 0.86)",
            backdropFilter: "blur(24px) saturate(150%)",
            borderBottom: "1px solid hsl(var(--border) / 0.65)",
            boxShadow: "0 16px 50px -48px var(--skeuo-shadow)",
          }}
        >
          <button
            onClick={() => setMobileOpen(true)}
            className="-ml-1 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent/30 hover:text-text md:hidden"
          >
            <Menu className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-muted/60 hidden md:block text-[11px]">Admin</span>
            {currentItem && (
              <>
                <ChevronRight className="w-3 h-3 text-muted/40 hidden md:block" />
                <span className="font-semibold text-text text-[12px]">{currentItem.label}</span>
              </>
            )}
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-1.5">
            <span className="w-[7px] h-[7px] rounded-full bg-success shadow-[0_0_6px_rgba(16,185,129,0.6)]" />
            <span className="text-[10px] text-muted/70 hidden sm:block font-medium">Live</span>
          </div>
        </header>

        <div className="mx-auto w-full max-w-[1440px] flex-1 p-4 pb-20 md:p-6 md:pb-8">
          <ErrorBoundary>
            <Suspense
              fallback={
                <div className="flex flex-col items-center justify-center py-32 gap-3">
                  <div className="w-7 h-7 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
                  <p className="text-xs text-muted-foreground">Loading...</p>
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>

      <nav
        className="md:hidden fixed bottom-0 inset-x-0 flex z-30 safe-area-inset-bottom"
        style={{
          background: "hsl(var(--card) / 0.94)",
          backdropFilter: "blur(24px) saturate(150%)",
          boxShadow: "0 -18px 50px -42px var(--skeuo-shadow), 0 1px 0 0 var(--skeuo-highlight) inset",
          borderTop: "1px solid hsl(var(--border) / 0.65)",
        }}
      >
        {MOBILE_NAV.map(({ to, label, icon: Icon, exact }) => {
          const active = matchActive(pathname, to, exact)
          return (
            <button
              key={to}
              onClick={() => navigate(to)}
              className={cn(
                'flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[9px] font-semibold transition-colors min-h-[52px]',
                active ? 'text-info' : 'text-muted-foreground',
              )}
            >
              <Icon className="h-[18px] w-[18px]" />
              <span>{label}</span>
            </button>
          )
        })}
      </nav>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}

// ─── Router ───────────────────────────────────────────────────────────────────
export const router = createBrowserRouter([
  { path: '/admin/login', element: <Suspense fallback={null}><LoginPage /></Suspense> },
  {
    path: '/admin',
    element: <ProtectedRoute><AdminLayout /></ProtectedRoute>,
    children: [
      { index: true,          element: <DashboardPage /> },
      { path: 'users',        element: <UsersPage /> },
      { path: 'slips',        element: <SlipsPage /> },
      { path: 'leagues',      element: <LeaguesPage /> },
      { path: 'predictions',  element: <PredictionsPage /> },
      { path: 'slip-review',  element: <SlipReviewPage /> },
      { path: 'analytics',    element: <AnalyticsPage /> },
      { path: 'subscriptions',element: <SubscriptionsPage /> },
      { path: 'referrals',    element: <ReferralsPage /> },
      { path: 'affiliates',   element: <AffiliateManagementPage /> },
      { path: 'saas-metrics', element: <SaaSMetricsPage /> },
      { path: 'winback',      element: <WinBackCampaignsPage /> },
      { path: 'trap',         element: <TrapAnalyticsPage /> },
      { path: 'feedback',     element: <FeedbackPage /> },
      { path: 'contact',      element: <ContactInboxPage /> },
      { path: 'refunds',      element: <RefundsPage /> },
      { path: 'arena-economy', element: <ArenaEconomyPage /> },
      { path: 'insights',     element: <InsightsPage /> },
      { path: 'calibration',  element: <CalibrationPage /> },
      { path: 'audit',        element: <AuditPage /> },
      { path: 'settings',     element: <SettingsPage /> },
      { path: '*',            element: <NotFoundPage /> },
    ],
  },
  // Unknown top-level paths fall through to a real 404 page so we don't
  // silently swallow typos by bouncing to /admin.
  { path: '*', element: <Suspense fallback={null}><NotFoundPage /></Suspense> },
])
